/* eslint-disable no-shadow */
import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import cx from 'classnames';
import moment from 'moment';

import AccountReservationCard, { getComputedMode } from '../AccountReservationCard';
import AccountNotifyCard from '../../AccountNotifyCard';
import Loader from '../../Loader';
import Notify from '../../Notify/Notify';
import { getPriorityNotifyExperiment } from '../../Notify/notifyUtils';
import ProfileCard, { PROFILE_CARD_PAGE_CONTEXT } from '../ProfileCard/ProfileCard';
import './AccountReservations.scss';
import '../AccountPage.scss';

import TranslationProvider from '../../../providers/TranslationProvider';
import Button from '../../Button';
import Link from '../../Link';
import GdaPromotional, { PROMOTION_CONTEXT } from '../../GdaPromotional';
import TockReservationsNotice from '../../TockReservationsNotice';
import { IDLE } from '../../../constants/asyncLoadingStatuses';
import { getVenueReservationsThunk, loadResyConfigThunk } from '../../../reducers';
import {
  getUserThunk,
  markReservationsChangedThunk,
  resetShouldUserReservationsUpdate,
  setReservationToBeModified,
} from '../../../reducers/userSlice';
import { getUserReservations } from '../../../api';
import { reservationsTransform } from '../../../api/resyApiTransformer';
import { getNotify } from '../../../api/api';
import ALLOWED_DOMAINS from '../../../constants/allowedDomains';
import { handleNavigate, handleNavigateWithParams } from '../../../utils/locationUtils';
import { setSignal } from '../../../reducers/signalSlice';
import Logger from '../../../utils/logger';
import {
  IS_JCE_RESERVATION_NOTIFY_DARK_ENABLED,
  IS_JCE_RESERVATION_NOTIFY_ENABLED,
} from '../../../constants/optimizelyExperiments';
import RouterNavigateContext from '../../../../../widget/app/modules/contexts/RouterNavigate';

export const UPCOMING_SECTION_KEY = 'upcoming';
const PAST_SECTION_KEY = 'past';
const RESERVATIONS_LIMIT = 10;
const NOTIFY_DISPLAY_MAX = 10;
const EVENT_SERVICE_TYPE_IDS = [6, 7, 8];

const accountReservationsPropTypes = {
  context: PropTypes.string,
  Analytics: PropTypes.shape({
    event: PropTypes.func,
  }).isRequired,
  $rootScope: PropTypes.shape({
    $broadcast: PropTypes.func.isRequired,
    matchMedia: PropTypes.shape({
      xs: PropTypes.bool,
      sm: PropTypes.bool,
      md: PropTypes.bool,
      lg: PropTypes.bool,
      ipadLandscape: PropTypes.bool,
      xl: PropTypes.bool,
    }).isRequired,
  }).isRequired,
  $route: PropTypes.shape({}).isRequired,
  bookOnBehalfOf: PropTypes.bool,
  configStatus: PropTypes.string.isRequired,
  dispatch: PropTypes.func.isRequired,
  flushRoute: PropTypes.func,
  getUserThunk: PropTypes.func.isRequired,
  isGdaEnabled: PropTypes.bool,
  isJceReservationNotifyDarkEnabled: PropTypes.bool,
  isJceReservationNotifyEnabled: PropTypes.bool,
  isPriorityNotifyExperimentActive: PropTypes.bool,
  loadReservations: PropTypes.func.isRequired,
  markReservationsChangedThunk: PropTypes.func,
  presentCancelation: PropTypes.func,
  resetShouldUserReservationsUpdate: PropTypes.func.isRequired,
  setReservationToBeModified: PropTypes.func.isRequired,
  setSignal: PropTypes.func.isRequired,
  shouldUserReservationsUpdate: PropTypes.string.isRequired,
  user: PropTypes.shape({
    bio: PropTypes.string,
    em_address: PropTypes.string.isRequired,
    first_name: PropTypes.string.isRequired,
    is_global_dining_access: PropTypes.bool.isRequired,
    last_name: PropTypes.string.isRequired,
    mobile_number: PropTypes.string.isRequired,
    notify_wins: PropTypes.number.isRequired,
    num_bookings: PropTypes.number.isRequired,
    profile_image_url: PropTypes.string,
    resy_member_since: PropTypes.number.isRequired,
  }).isRequired,
  venueReservationsList: PropTypes.arrayOf(PropTypes.shape({})),
  getVenueReservationsThunk: PropTypes.func.isRequired,
};

class AccountReservations extends Component {
  static propTypes = accountReservationsPropTypes;

  static defaultProps = {
    context: '',
    bookOnBehalfOf: false,
    flushRoute: () => { },
    isGdaEnabled: false,
    isJceReservationNotifyDarkEnabled: true,
    isJceReservationNotifyEnabled: false,
    isPriorityNotifyExperimentActive: false,
    markReservationsChangedThunk: () => {},
    presentCancelation: /* istanbul ignore next */ () => {},
    venueReservationsList: [],
  };

  constructor(props) {
    super(props);

    this.ref = React.createRef();
  }

  state = {
    isLoadingInitial: true,
    upcoming: {
      hasError: false,
      showMoreProcessing: false,
      showMoreBtn: false,
      offset: 1,
      reservations: [],
    },
    past: {
      hasError: false,
      showMoreProcessing: false,
      showMoreBtn: false,
      offset: 1,
      reservations: [],
    },
    notify: {
      activeNotifications: undefined,
      hasError: false,
      isModalOpen: false,
      notifyService: undefined,
      offset: 1,
      notifies: [],
      selectedDate: undefined,
      selectedQuantity: undefined,
      serviceTypeId: undefined,
      showMoreBtn: false,
      showMoreProcessing: false,
      venueId: undefined,
      venueName: undefined,
    },
  };

  async componentDidMount() {
    const {
      configStatus,
      dispatch,
      shouldUserReservationsUpdate,
      getUserThunk,
    } = this.props;

    if (configStatus === IDLE) {
      dispatch(loadResyConfigThunk());
    }
    this.getUserPromise = getUserThunk(true, { scheduleFollowUp: true });
    this.upcomingLoadingPromise = this.fetchReservations(UPCOMING_SECTION_KEY, true);
    this.notifyLoadingPromise = this.fetchNotify();
    this.pastLoadingPromise = this.fetchReservations(PAST_SECTION_KEY, true);

    const [user] = await Promise.all([
      this.getUserPromise,
      this.upcomingLoadingPromise,
      this.notifyLoadingPromise,
      this.pastLoadingPromise,
    ]);
    this.setState(
      {
        isLoadingInitial: false,
        isGdaUser: !!user?.is_global_dining_access,
      },
      () => {
        this.ref.current?.focus({ preventScroll: true });
      }
    );
    window.addEventListener('scroll', this.handleMorePastReservations);
    window.addEventListener('message', this.refreshReservations);

    // Replacement for Angular User.reservations.load() and USER_RESERVATIONS_UPDATE event call and response
    // to allow external callers to indicate user reservations refresh needed.
    if (shouldUserReservationsUpdate) {
      this.handleShouldUserReservationsUpdate(shouldUserReservationsUpdate);
    }
  }

  async componentDidUpdate(prevProps) {
    const {
      shouldUserReservationsUpdate, venueReservationsList, bookOnBehalfOf,
    } = this.props;

    // catches changes to bookOnBehalfOf prop to refetch reservations in widget
    if (prevProps.bookOnBehalfOf !== bookOnBehalfOf) {
      this.updateUserReservations({ type: UPCOMING_SECTION_KEY });
      this.fetchNotify();
      this.updateUserReservations({ type: PAST_SECTION_KEY });
    }

    if (prevProps.venueReservationsList !== venueReservationsList) {
      this.handleVenueReservationsFetched(venueReservationsList);
    }

    // Replacement for Angular User.reservations.load() and USER_RESERVATIONS_UPDATE event call and response
    // to allow external callers to indicate user reservations refresh needed.
    if (shouldUserReservationsUpdate) {
      this.handleShouldUserReservationsUpdate(shouldUserReservationsUpdate);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('scroll', this.handleMorePastReservations);
    window.removeEventListener('message', this.refreshReservations);
  }

  fetchNotify = async () => {
    try {
      const { notify } = await getNotify();
      const showMoreBtn = notify?.length > NOTIFY_DISPLAY_MAX;
      this.setState({
        notify: {
          notifies: notify,
          hasError: false,
          showMoreBtn,
        },
      });
    } catch (error) {
      Logger.error('[Notify] error fetching notify');
    }
  }

  fetchReservations = async (section, force = false) => {
    const { bookOnBehalfOf, context } = this.props;
    const {
      [section]: {
        reservations: currentReservations,
        offset,
      },
    } = this.state;
    const { [section]: currState } = this.state;

    if (!force && (!currState.showMoreBtn || currState.showMoreProcessing)) return;

    this.setState({ [section]: { ...currState, showMoreProcessing: true } });

    try {
      const payload = {
        limit: RESERVATIONS_LIMIT,
        offset,
        type: section,
        book_on_behalf_of: bookOnBehalfOf,
      };

      if (context === 'widget' && section === PAST_SECTION_KEY) {
        payload.limit = 10000;
      }

      const responseData = await getUserReservations(payload);
      const reservations = reservationsTransform(responseData);
      const totalReservationsLoaded = currentReservations.length + reservations.length;
      this.setState({
        [section]: {
          offset: offset + 1,
          reservations: [
            ...currentReservations,
            ...reservations,
          ],
          hasError: false,
          showMoreProcessing: false,
          showMoreBtn: responseData.metadata.total > totalReservationsLoaded,
        },
      });
    } catch (err) {
      this.setState({
        [section]: {
          hasError: true,
          showMoreProcessing: false,
          showMoreBtn: false,
        },
      });
    }
  }

  loadReservations = (venueId, selectedDate, selectedQuantity, resyToken) => {
    const { getVenueReservationsThunk } = this.props;
    const params = {
      venue_id: venueId,
      day: moment(selectedDate).format('YYYY-MM-DD'),
      party_size: selectedQuantity,
      resy_token: resyToken,
    };
    return getVenueReservationsThunk({ params });
  };

  // The flag is raised after a reservation changes (cancelled from this list or in the widget),
  // but also on login or after rating a past reservation. getUserThunk(false) only refetches /2/user
  // if a reservation change was recorded since the last fetch, so num_bookings catches up after a
  // cancel without extra fetches for the other cases.
  handleShouldUserReservationsUpdate = (params) => {
    const { bookOnBehalfOf, getUserThunk } = this.props;
    // Concierge lists show other guests' reservations, which don't change the user's own count
    if (!bookOnBehalfOf) {
      getUserThunk(false, { scheduleFollowUp: true }).catch(() => {});
    }
    this.updateUserReservations(params);
  }

  // Migration of Angular User.reservations.load and USER_RESERVATIONS_UPDATE event call and response
  updateUserReservations = (params) => {
    const { resetShouldUserReservationsUpdate, bookOnBehalfOf, context } = this.props;

    const payload = {
      limit: RESERVATIONS_LIMIT,
      offset: 1,
      book_on_behalf_of: bookOnBehalfOf,
      ...params,
    };

    if (context === 'widget' && payload?.type === PAST_SECTION_KEY) {
      payload.limit = 10000;
    }

    return getUserReservations(payload)
      .then((responseData) => {
        const transformedData = {
          reservations: reservationsTransform(responseData),
          metadata: {
            ...responseData.metadata,
            type: payload.type || 'all',
          },
        };
        this.handleReservationsUpdate(transformedData);
        resetShouldUserReservationsUpdate();
      })
      .catch((err) => {
        if (err && err.status !== 419) {
          Logger.error('[CATCH] getUserReservations()', err);
        }
      });
  }

  handleCardClicked = (reservation) => {
    const { context } = this.props;
    if (context === 'widget') {
      this.handleWidgetCardClicked(reservation);
    } else {
      this.handleConsumerCardClicked(reservation);
    }
  }

  handleConsumerCardClicked = (reservation) => {
    const { bookOnBehalfOf } = this.props;
    const venueId = reservation.venue.id;
    const resyToken = reservation.token;
    const { conciergeResyToken } = reservation;
    const params = {
      venueId,
      src: 'resy.com-reservations',
    };

    if (bookOnBehalfOf) {
      params.conciergeResyToken = conciergeResyToken;
    } else {
      params.resyToken = resyToken;
    }

    window.resyWidget.openModal(params);
  }

  handleWidgetCardClicked = async (upcomingReservation) => {
    const {
      flushRoute, setReservationToBeModified,
    } = this.props;

    const payload = upcomingReservation.bookOnBehalf ? {
      concierge_resy_token: upcomingReservation.conciergeResyToken,
      book_on_behalf_of: 1,
    }
      : {
        resy_token: upcomingReservation.token,
      };
    // The existing reservation object was fetched by requesting `upcoming` bookings,
    // and doesn't include add-on data which is needed by the confirmation view.
    // Fetching that single reservation using a resy token results in getting the full reservation data
    const data = await getUserReservations(payload);
    const reservations = reservationsTransform(data);

    setReservationToBeModified(reservations[0]);
    const routerNavigate = this.context;
    handleNavigate('/account/change-cancel', routerNavigate, flushRoute);
  }

  handleRatingClicked = (reservation) => {
    const { flushRoute } = this.props;
    const routerNavigate = this.context;
    const params = new URLSearchParams({
      resyToken: reservation.token,
      venue: reservation.venue.name,
      rate_referrer: 'account',
    });
    handleNavigateWithParams('/rate', params, routerNavigate, flushRoute);
  }

  handleMorePastReservations = () => {
    const { past: { showMoreBtn, showMoreProcessing } } = this.state;
    if (!showMoreBtn || showMoreProcessing) return;
    if ((window.innerHeight + window.scrollY) >= document.body.offsetHeight - 300) {
      this.fetchReservations(PAST_SECTION_KEY);
    }
  }

  refreshReservations = (message) => {
    if (!ALLOWED_DOMAINS.includes(message.origin)) {
      return;
    }

    if (message?.data === 'reloadReservations') {
      const { markReservationsChangedThunk } = this.props;
      // Persisted, so the reloaded page still knows /2/user may lag behind
      markReservationsChangedThunk();
      window.location.reload();
    }
  }

  handleReservationsUpdate = (data) => {
    const { metadata: { total, type }, reservations } = data;
    if (type === UPCOMING_SECTION_KEY) {
      const { upcoming } = this.state;
      const showMoreBtn = total > reservations.length;
      const upcomingState = {
        ...upcoming,
        reservations,
        showMoreBtn,
      };
      this.setState({ [UPCOMING_SECTION_KEY]: upcomingState });
    } else if (type === PAST_SECTION_KEY) {
      const { past } = this.state;
      const showMoreBtn = total > reservations.length;
      const pastState = {
        ...past,
        reservations,
        showMoreBtn,
      };
      this.setState({ [PAST_SECTION_KEY]: pastState });
    }
  }

  handleVenueReservationsFetched = (venueReservationsList) => {
    const { notify: currState } = this.state;
    this.setState({ notify: { ...currState, reservations: venueReservationsList } });
  }

  renderTitleHeading = () => {
    const {
      $rootScope,
      bookOnBehalfOf,
      user,
    } = this.props;
    const isMobile = !$rootScope?.matchMedia?.sm === true;

    if (!user) {
      return null;
    }

    if (bookOnBehalfOf) {
      return (
        <>
          <ProfileCard
            context={PROFILE_CARD_PAGE_CONTEXT.RESERVATIONS}
            joinDate={user.resy_member_since}
            reservations={user.num_bookings}
            notifyWins={user.notify_wins}
            isGDAUser={user.is_global_dining_access}
            isMobile={isMobile}
            imageURL={user.profile_image_url}
            user={{
              bio: user.bio,
              emAddress: user.em_address,
              firstName: user.first_name,
              lastName: user.last_name,
              mobileNumber: user.mobile_number,
            }}
          />
          <TranslationProvider translationKey="upcoming_concierge_reservations">
            <h1 className="AccountPage__title AccountReservations__title">
              Upcoming Concierge Reservations
            </h1>
          </TranslationProvider>
        </>
      );
    }

    return (
      <>
        <ProfileCard
          context={PROFILE_CARD_PAGE_CONTEXT.RESERVATIONS}
          joinDate={user.resy_member_since}
          reservations={user.num_bookings}
          notifyWins={user.notify_wins}
          isGDAUser={user.is_global_dining_access}
          isMobile={isMobile}
          imageURL={user.profile_image_url}
          user={{
            bio: user.bio,
            emAddress: user.em_address,
            firstName: user.first_name,
            lastName: user.last_name,
            mobileNumber: user.mobile_number,
          }}
        />
        <TranslationProvider translationKey="upcoming_reservations">
          <h1 className="AccountPage__title AccountReservations__title">
            Upcoming Reservations
          </h1>
        </TranslationProvider>
      </>
    );
  }

  renderConciergeNotice = () => {
    const {
      bookOnBehalfOf,
      isJceReservationNotifyDarkEnabled,
      isJceReservationNotifyEnabled,
    } = this.props;

    if (
      !bookOnBehalfOf
      || !isJceReservationNotifyEnabled
      || isJceReservationNotifyDarkEnabled
    ) {
      return null;
    }

    return (
      <p className="AccountReservations__concierge-notice">
        <TranslationProvider translationKey="concierge_reservations_notice_body">
          <span>Concierge status and reservations are managed separately on Resy and Tock. View and manage your Resy concierge reservations here and your Tock concierge reservations on</span>
        </TranslationProvider>
        {' '}
        <Link
          className="AccountReservations__concierge-notice-link"
          href="https://www.exploretock.com"
          target="_blank"
          rel="noopener"
          variant="inline"
        >
          ExploreTock.com
        </Link>
        .
      </p>
    );
  }

  renderGdaPromotional = () => {
    const { isGdaUser } = this.state;
    const {
      Analytics, isGdaEnabled,
    } = this.props;

    if (isGdaEnabled && !isGdaUser) {
      return (
        <>
          <GdaPromotional
            Analytics={Analytics}
            variant={PROMOTION_CONTEXT.profile}
          />
        </>
      );
    }
    return null;
  }

  renderNotifyHeading = () => {
    const { isGdaUser, notify } = this.state;
    const { isPriorityNotifyExperimentActive } = this.props;

    // The priority notify experiment always uses the standard Notify heading, regardless of GDA status.
    if (!isPriorityNotifyExperimentActive && isGdaUser) {
      return (
        <>
          <TranslationProvider translationKey="priority_notify">
            <h2 className="AccountPage__title AccountReservations__title">
              Priority Notify
            </h2>
          </TranslationProvider>
          {notify.notifies?.length === 0 && this.renderNoNotifyMessage()}
        </>
      );
    }
    return (
      <>
        <TranslationProvider translationKey="notify">
          <h2 className="AccountPage__title AccountReservations__title">
            Notify
          </h2>
        </TranslationProvider>
        {notify.notifies?.length === 0 && this.renderNoNotifyMessage()}
      </>
    );
  }

  renderNoNotifyMessage = () => {
    const { isGdaUser } = this.state;

    /* eslint-disable max-len */
    if (isGdaUser) {
      return (
        <div className="AccountReservations__no-reservations AccountReservations__no-reservations--no-notifies">
          <TranslationProvider translationKey="no_priority_notify_set">
            You currently don’t have Notify set. Priority Notify gives you priority notifications at over 9,000 restaurants. If the reservation or ticket you want isn’t available, set a Notify and you will receive a notification when something opens up.
          </TranslationProvider>
        </div>
      );
    }
    return (
      <div className="AccountReservations__no-reservations AccountReservations__no-reservations--no-notifies">
        <TranslationProvider className="AccountReservations__no-notifies" translationKey="no_notify_set">
          You currently don’t have Notify set. If the reservation or ticket you want isn’t available, set a Notify and you will receive a notification when something opens up. Download the Resy app to receive a real-time notification if a reservation becomes available.
        </TranslationProvider>
      </div>
    );
    /* eslint-enable max-len */
  }

  renderNotifyCards = () => {
    const {
      $rootScope,
      $route,
      context,
    } = this.props;
    const { notify } = this.state;

    const notifiesByVenue = {};

    notify.notifies?.forEach((eachNotify) => {
      const venueId = eachNotify.specs?.venue_id;
      if (eachNotify.specs?.service_type_id && EVENT_SERVICE_TYPE_IDS.includes(eachNotify.specs?.service_type_id)) {
        /**
         * Each event needs to go on its own, outside of the venue node.
         * Adding the date and time to the key will prevent events that are same venue or same venue
         * and same date from getting overwritten as we loop through the notifies.
         */
        notifiesByVenue[
          `${venueId}-event-${eachNotify.specs?.day}-${eachNotify.specs?.time_preferred_start}`
        ] = {
          earliestDate: eachNotify.specs?.day,
          earliestStartTime: eachNotify.specs?.time_preferred_start,
          notifies: [eachNotify.specs],
          venue: eachNotify.results?.venues[0].venue,
        };
      } else if (notifiesByVenue[venueId]) {
        notifiesByVenue[venueId].notifies.push(eachNotify.specs);
        /**
         * have to spread the events data into the venue because each notify result has its
         * own instance of the venue object with only the events for that specific date
         * */
        notifiesByVenue[venueId].venue?.events?.push(...eachNotify.results?.venues[0].venue.events);
      } else {
        notifiesByVenue[venueId] = {
          earliestDate: eachNotify.specs?.day,
          earliestStartTime: eachNotify.specs?.time_preferred_start,
          notifies: [eachNotify.specs],
          venue: eachNotify.results?.venues[0].venue,
        };
      }
    });

    const sortedNotifies = Object.values(notifiesByVenue);

    sortedNotifies.sort((a, b) => {
      if (a.earliestDate !== b.earliestDate) {
        if (moment(a.earliestDate).isBefore(b.earliestDate)) {
          return -1;
        }
        return 1;
      }
      return 0;
    });

    let sortedNotifiesDisplayList = [];
    if (notify.showMoreBtn) {
      let remainingCount = NOTIFY_DISPLAY_MAX;
      for (let v = 0; v < sortedNotifies.length; v += 1) {
        sortedNotifiesDisplayList.push(sortedNotifies[v]);

        const notifyCount = sortedNotifies[v].notifies.length;
        if (notifyCount <= remainingCount) {
          remainingCount -= notifyCount;
          if (remainingCount === 0) {
            break;
          }
        } else {
          const truncatedNotifies = sortedNotifiesDisplayList[v].notifies.slice(0, remainingCount);
          sortedNotifiesDisplayList[v].notifies = truncatedNotifies;
          break;
        }
      }
    } else {
      sortedNotifiesDisplayList = sortedNotifies;
    }

    return sortedNotifiesDisplayList.map((reservation) => {
      const {
        name: venueName,
        events: venueEvents,
        id,
        responsive_images: images,
        url_slug: venueUrl,
        location,
      } = reservation.venue;

      const imageUrl = images?.urls[images.file_names[0]] && images.urls[images.file_names[0]]['1:1'][200];

      return (
        <AccountNotifyCard
          $rootScope={$rootScope}
          $route={$route}
          activeNotifications={reservation.notifies}
          context={context}
          image={imageUrl}
          key={`${id.resy}-${reservation.notifies[0].service_type_id}`}
          locationSlug={location.url_slug}
          toggleWidgetNotifyModal={this.toggleWidgetNotifyModal}
          venueId={id.resy}
          venueCityCode={location.code}
          venueEvents={venueEvents}
          venueName={venueName}
          venueUrl={venueUrl}
        />
      );
    });
  }

  handleShowMoreNotifyClicked = () => {
    const { notify: currState } = this.state;
    this.setState({ notify: { ...currState, showMoreBtn: false } });
  }

  toggleWidgetNotifyModal = (payload) => {
    const { loadReservations } = this.props;
    const { notify: currState } = this.state;

    if (currState.venueId !== payload.venueId) {
      const loadVenueReservations = loadReservations || this.loadReservations;
      loadVenueReservations(payload.venueId, payload.selectedDate, payload.selectedQuantity);
    }
    this.setState({ notify: { ...currState, ...payload, isModalOpen: !currState.isModalOpen } });
  }

  closeNotify = () => {
    const { $rootScope, context, setSignal } = this.props;
    const { notify: currState } = this.state;
    this.setState({ notify: { ...currState, isModalOpen: false } });
    if (context === 'widget') {
      // As Angular.js Widget migration occurs, we need to emit a signal from both
      // Angular $broadcast and Redux setSignal for this shared component.
      $rootScope.$broadcast('TOGGLE_MODAL');
      setSignal({ name: 'TOGGLE_MODAL' });
    }
  }

  render() {
    const {
      $rootScope,
      $route,
      Analytics,
      context,
      bookOnBehalfOf,
      loadReservations,
      presentCancelation,
      venueReservationsList,
    } = this.props;
    const {
      notify,
      upcoming,
      past,
      isLoadingInitial,
      isGdaUser,
    } = this.state;
    const containerClasses = cx('AccountPage AccountReservations', {
      AccountPageWidget: context === 'widget',
    });

    if (isLoadingInitial) {
      return (
        <div className={containerClasses}>
          {this.renderTitleHeading()}
          <Loader className="AccountPage__loader" />
        </div>
      );
    }

    if (upcoming.hasError && past.hasError) {
      return (
        <div className={containerClasses}>
          {this.renderTitleHeading()}
          <TranslationProvider translationKey="generic_error">
            <div className="AccountReservations__no-reservations">
              Something went wrong
            </div>
          </TranslationProvider>
        </div>
      );
    }

    if (!isLoadingInitial && upcoming.reservations?.length === 0 && past.reservations?.length === 0 && notify.notifies?.length === 0) {
      return (
        <div className={containerClasses}>
          <div className="AccountReservations__no-reservations--category">
            {this.renderTitleHeading()}
            {this.renderConciergeNotice()}
            <TranslationProvider translationKey="no_upcoming_reservations">
              <div className="AccountReservations__no-reservations">
                You don’t have any upcoming reservations
              </div>
            </TranslationProvider>
          </div>
          {this.renderGdaPromotional()}
          <TockReservationsNotice />
          {this.renderNotifyHeading()}
        </div>
      );
    }
    return (
      <>
        {context === 'widget' && (
          <Notify
            {...notify}
            $scope={$rootScope}
            $route={$route}
            $window={window}
            Analytics={Analytics}
            context={context}
            isGda={isGdaUser}
            isOpen={notify.isModalOpen}
            loadReservations={loadReservations || this.loadReservations}
            locationSlug={notify.locationSlug}
            openModal={this.closeNotify}
            reservations={venueReservationsList}
          />
        )}
        <div className={containerClasses} ref={this.ref} tabIndex="-1">
          <div className="AccountReservations__upcoming">
            {this.renderTitleHeading()}
            {this.renderConciergeNotice()}
            {upcoming.reservations?.length === 0 && (
              <TranslationProvider translationKey="no_upcoming_reservations">
                <p className="AccountReservations__no-reservations">
                  You don’t have any upcoming reservations.
                </p>
              </TranslationProvider>
            )}
            {upcoming.reservations?.length > 0 && (
              upcoming.reservations.map((reservation) => (
                <AccountReservationCard
                  key={reservation.id}
                  reservation={reservation}
                  mode={getComputedMode(context, reservation)}
                  onRating={this.handleRatingClicked}
                  onClick={this.handleCardClicked}
                  isGda={reservation.isGda && isGdaUser}
                  bookOnBehalfOf={reservation.bookOnBehalf}
                  guestName={reservation.bookOnBehalf?.name}
                  presentCancelation={presentCancelation}
                  context={context}
                />
              ))
            )}
            {upcoming.showMoreBtn && (
              <Button
                onClick={() => this.fetchReservations(UPCOMING_SECTION_KEY)}
                className="AccountReservations__upcoming-button"
                processing={upcoming.showMoreProcessing}
                variant="secondary"
              >
                <TranslationProvider translationKey="show_more">
                  <span>Show More</span>
                </TranslationProvider>
              </Button>
            )}
          </div>
          {this.renderGdaPromotional()}
          <TockReservationsNotice />
          <div className="AccountReservations__notify">
            {this.renderNotifyHeading()}
            {this.renderNotifyCards()}
            {notify.showMoreBtn && (
              <Button
                onClick={this.handleShowMoreNotifyClicked}
                className="AccountReservations__upcoming-button"
                variant="secondary"
              >
                <TranslationProvider translationKey="show_more">
                  <span>Show More</span>
                </TranslationProvider>
              </Button>
            )}
          </div>

          <div className="AccountReservations__past">
            {!bookOnBehalfOf && (
              <TranslationProvider translationKey="past_reservations">
                <h2 className="AccountPage__title AccountReservations__title">
                  Past Reservations
                </h2>
              </TranslationProvider>
            )}
            {bookOnBehalfOf && (
              <h1 className="AccountPage__title AccountReservations__title">
                Past Concierge Reservations
              </h1>
            )}
            {past.reservations?.length === 0 && (
              <TranslationProvider translationKey="no_reservations">
                <p className="AccountReservations__no-reservations">
                  You don’t have any past reservations.
                </p>
              </TranslationProvider>
            )}
            {past.reservations?.length > 0 && (
              past.reservations.map((reservation) => (
                <AccountReservationCard
                  past
                  key={reservation.id}
                  reservation={reservation}
                  mode={getComputedMode(context, reservation)}
                  onRating={this.handleRatingClicked}
                  onClick={this.handleCardClicked}
                  isGda={reservation.isGda && isGdaUser}
                  bookOnBehalfOf={reservation.bookOnBehalf}
                  guestName={reservation.bookOnBehalf?.name}
                  presentCancelation={presentCancelation}
                  context={context}
                />
              ))
            )}
            {past.showMoreProcessing && (
              <Loader className="AccountPage__loader" />
            )}
          </div>
        </div>
      </>
    );
  }
}

AccountReservations.contextType = RouterNavigateContext;

const mapStateToProps = (state) => {
  const {
    resyConfig, user, venueReservations,
  } = state;

  return {
    configStatus: resyConfig.configStatus,
    isGdaEnabled: resyConfig.config?.enable_global_dining_access,
    isJceReservationNotifyDarkEnabled:
      state.experiments?.[IS_JCE_RESERVATION_NOTIFY_DARK_ENABLED]?.enabled,
    isJceReservationNotifyEnabled:
      state.experiments?.[IS_JCE_RESERVATION_NOTIFY_ENABLED]?.enabled,
    isPriorityNotifyExperimentActive: getPriorityNotifyExperiment(state).isActive,
    shouldUserReservationsUpdate: user.shouldUserReservationsUpdate,
    user: user.userData,
    venueReservationsList: venueReservations.data?.reservations,
  };
};

const mapDispatchToProps = {
  getUserThunk,
  getVenueReservationsThunk,
  markReservationsChangedThunk,
  setReservationToBeModified,
  setSignal,
  resetShouldUserReservationsUpdate,
};

AccountReservations.displayName = 'AccountReservations';

export { accountReservationsPropTypes, AccountReservations as ComponentUnderTest, mapStateToProps };
export default connect(mapStateToProps, mapDispatchToProps)(AccountReservations);
